/**
 * Announcements API
 * RTK Query endpoints for announcement management
 */

import { baseApi } from "./baseApi";

/**
 * Announcement interface
 */
export interface Announcement {
	id: string;
	organization_id?: string;
	title: string;
	content: string;
	priority: "info" | "warning" | "success" | "urgent";
	status: "draft" | "active" | "archived";
	target_roles: string[];
	target_property_ids: string[];
	target_property_names: string[];
	target_lease_ids: string[];
	target_lease_codes: string[];
	publish_date?: string;
	expiry_date?: string;
	action_link?: string;
	action_text?: string;
	created_by_id: string;
	created_by_name?: string;
	email_sent: boolean;
	created_at: string;
	updated_at: string;
	published_at?: string;
	is_active: boolean;
	target_count?: number;
}

/**
 * Announcement list response interface
 */
export interface AnnouncementListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Announcement[];
}

/**
 * Create/Update announcement request interface
 */
export interface AnnouncementData {
	title: string;
	content: string;
	priority?: "info" | "warning" | "success" | "urgent";
	status?: "draft" | "active" | "archived";
	target_roles?: string[];
	target_property_ids?: string[];
	target_lease_ids?: string[];
	publish_date?: string;
	expiry_date?: string;
	action_link?: string;
	action_text?: string;
	send_email?: boolean;
}

/**
 * Announcements API endpoints
 */
export const announcementsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all announcements (filtered by user role and targeting)
		 */
		listAnnouncements: builder.query<
			AnnouncementListResponse,
			{
				page?: number;
				page_size?: number;
				status?: string;
				priority?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.status) queryParams.append("status", params.status);
				if (params.priority) queryParams.append("priority", params.priority);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				const queryString = queryParams.toString();
				return {
					url: queryString
						? `/announcements/?${queryString}`
						: "/announcements/",
					method: "GET",
				};
			},
			providesTags: ["Announcements"],
		}),

		/**
		 * Get announcement details by ID
		 */
		getAnnouncement: builder.query<Announcement, string>({
			query: (id) => ({
				url: `/announcements/${id}`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "Announcements", id }],
		}),

		/**
		 * Create a new announcement (admin only)
		 */
		createAnnouncement: builder.mutation<Announcement, AnnouncementData>({
			query: (data) => ({
				url: "/announcements/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Announcements"],
		}),

		/**
		 * Update an announcement (admin only)
		 */
		updateAnnouncement: builder.mutation<
			Announcement,
			{ id: string; data: Partial<AnnouncementData> }
		>({
			query: ({ id, data }) => ({
				url: `/announcements/${id}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Announcements", id },
				"Announcements",
			],
		}),

		/**
		 * Delete an announcement (admin only)
		 */
		deleteAnnouncement: builder.mutation<void, string>({
			query: (id) => ({
				url: `/announcements/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Announcements"],
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListAnnouncementsQuery,
	useLazyListAnnouncementsQuery,
	useGetAnnouncementQuery,
	useLazyGetAnnouncementQuery,
	useCreateAnnouncementMutation,
	useUpdateAnnouncementMutation,
	useDeleteAnnouncementMutation,
} = announcementsApi;
